/**
 * useAppVersionPoll — detects deploys that changed the frontend bundle.
 *
 * Complements the existing Service Worker "updatefound" flow in
 * usePWA.js. That flow only fires when the sw.js BYTES change (someone
 * bumped APP_VERSION in sw.js). A deploy that ships new React code but
 * leaves sw.js untouched — the common case — would otherwise go
 * unnoticed by the user until they manually reload. This hook closes
 * that gap.
 *
 * How it works:
 *   1. On mount, fetch /version.json once. Remember it as the baseline
 *      ("the version this tab booted with").
 *   2. Every 5 minutes, re-fetch /version.json. If the value differs
 *      from the baseline, set updateAvailable=true.
 *   3. Re-check immediately on tab-wake (visibilitychange) and network
 *      reconnect — a coach who's been away for hours shouldn't have to
 *      wait up to 5 more minutes to see the banner.
 *
 * Fetches use `cache: 'no-store'` so neither the browser HTTP cache
 * nor an upstream CDN can serve a stale version.json. Service Worker
 * fetch handler doesn't match /version.json either (it only intercepts
 * /api/* and static-asset extensions), so the SW can't cache it.
 *
 * Dev-mode safety: in `vite dev` there's no dist/ — no version.json
 * exists. The hook no-ops silently on 404 instead of thrashing toasts.
 */
import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000;          // 5 minutes
const VERSION_URL      = '/version.json';

async function fetchVersion() {
  try {
    const res = await fetch(VERSION_URL, {
      cache: 'no-store',
      // Don't send cookies — version.json is a static asset.
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const body = await res.json();
    // Accept either { version: "..." } or a bare string for flexibility.
    if (typeof body === 'string') return body;
    return body?.version || null;
  } catch {
    return null;
  }
}

export function useAppVersionPoll() {
  const [baselineVersion,  setBaselineVersion]  = useState(null);
  const [updateAvailable,  setUpdateAvailable]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timerId = null;

    const check = async () => {
      const v = await fetchVersion();
      if (cancelled) return;
      if (!v) return;                                  // couldn't fetch — skip
      if (baselineVersion === null) {
        setBaselineVersion(v);
      } else if (v !== baselineVersion) {
        setUpdateAvailable(true);
      }
    };

    // Initial fetch + interval
    check();
    timerId = setInterval(check, POLL_INTERVAL_MS);

    // Re-check on tab-wake and reconnect
    const onVis = () => { if (!document.hidden) check(); };
    const onOnline = () => check();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onVis);

    return () => {
      cancelled = true;
      if (timerId) clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onVis);
    };
  }, [baselineVersion]);

  return {
    updateAvailable,
    apply: () => window.location.reload(),
  };
}
